/**
 * Chain, settlement and relayer configuration.
 *
 * `isOnchainConfigured()` gates everything: while `gameAddress` is empty the
 * game stays in local/demo mode and never routes a play through the relayer.
 */

/** ERC-20 address zero — the contract's sentinel for native ETH. */
export const NATIVE_ADDRESS = "0x0000000000000000000000000000000000000000";

export const CHAIN_CONFIG = {
  /** ScratchyGame — Robinhood Chain mainnet test deployment. */
  gameAddress: "0x330572f30EAF6c429113Cc217109Cfb99fDc3828",

  /**
   * The contract this one replaced.
   *
   * Kept, and kept live, because ten addresses still hold balances there and
   * only one of them is ours — the rest found the public URL and played.
   * withdraw() carries no pause modifier, so those balances stay recoverable
   * forever; the game offers them a way out rather than assuming everyone
   * emptied their balance before the switch.
   */
  legacyGameAddress: "0x162c0b3649820c8e60e30ae33f1cc000c94fec99",

  chainId: "0x1237", // 4663 Robinhood Chain mainnet
  chainIdNumber: 4663,
  chainName: "Robinhood Chain",
  /* publicnode, not the chain's official endpoint. Measured on 2026-09-20:
     the official RPC updates its view of "latest" in jumps of five blocks
     every ~500ms, so a balance read straight after a play can return the
     balance from BEFORE it — the "my balance didn't update after a win"
     report. This endpoint advances one block per 100ms and allows browser
     origins. The relayer's CSP must name whichever host is here; it derives
     that from its own RPC list, so keep the two in step. */
  rpcUrl: "https://robinhood-rpc.publicnode.com",
  /**
   * Endpoint for history, which is a different job from reading the head.
   *
   * The wins feed asks for every Played event since the deploy block, and
   * publicnode refuses a range that wide: "Archive requests require a personal
   * token." So pointing the whole page at publicnode for its latency silently
   * emptied the feed — the fast endpoint simply cannot answer this question.
   * The chain's own RPC can, and its batched view of the head (the reason the
   * live reads moved away) does not matter for history that is thousands of
   * blocks old.
   *
   * Both hosts must appear in connect-src, in public/_headers AND in the
   * relayer's csp.ts, or the browser blocks whichever is missing.
   */
  archiveRpcUrl: "https://rpc.mainnet.chain.robinhood.com",
  /* Blockscout directly, NOT explorer.mainnet.chain.robinhood.com. That host
     301s to this one but DROPS the path, so every "verify" chip in the wins
     feed and every receipt link landed on the explorer homepage instead of the
     transaction. A QA tester followed one, inspected whatever they found
     there, and reported the feed as fabricated on the strength of it. */
  explorer: "https://robinhoodchain.blockscout.com",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },

  /**
   * Relayer service (contracts/relayer).
   *
   * Empty means SAME ORIGIN — the relayer is serving this page, so `/play` is
   * a relative path. That is the single-service deployment, and it is the
   * default because it needs no CORS, no build-time URL and no second host.
   *
   * Local development sets VITE_RELAYER_URL in .env.development, since Vite
   * serves the page on :5173 while the relayer listens on :8787. A split
   * deployment sets it too.
   */
  relayerUrl: import.meta.env.VITE_RELAYER_URL ?? "",

  /* Public by design: WalletConnect project ids ship in the client bundle and
     identify the app to the relay. Not a secret, and it belongs in the repo. */
  walletConnectProjectId: "bfd24692e205914e988713e12b7f3298"
};

/**
 * A settlement asset.
 *
 * The contract prices every tier in one table and scales it per currency, so
 * the only thing that differs here is denomination: how many wei make a
 * display unit, and what to call the unit on screen.
 */
export interface SettlementAsset {
  /** Stable key, used for storage and the picker. */
  id: string;
  address: string;
  isNative: boolean;
  decimals: number;
  /** The asset actually settled. */
  symbol: string;
  /** Shown on the picker chip. */
  label: string;
  /**
   * Denomination used for display only.
   *
   * At these tier sizes ETH means six leading zeros on every label, which is
   * unreadable and easy to misread by a factor of ten. Each asset picks a
   * denomination that keeps card prices as legible whole numbers. Settlement
   * is unchanged — this is purely how amounts are rendered.
   */
  display: { symbol: string; weiPer: bigint };
  /**
   * Wei per display unit — one "dollar" of the frontend's tier table.
   *
   * MUST equal UNIT * unitScale for this currency on-chain, where UNIT is 1e12
   * and unitScale is what setCurrency() registered. If they drift, the UI
   * shows one number while the chain charges another.
   */
  unitWei: bigint;
  /** Surfaced in the picker when the asset carries issuer risk. */
  warning?: string;
}

/** UNIT in contracts/script/MigrateToMultiCurrency.s.sol. */
const UNIT_WEI = 1_000_000_000_000n;

/**
 * What the house accepts, mirroring the on-chain registry.
 *
 * Each `unitWei` is UNIT_WEI multiplied by the scale registered on-chain, so a
 * card costs the same number of display units in every asset — 1,000 for a
 * Quick Hit — and only the denomination changes.
 */
export const CURRENCIES: SettlementAsset[] = [
  {
    id: "ETH",
    address: NATIVE_ADDRESS,
    isNative: true,
    decimals: 18,
    symbol: "ETH",
    label: "ETH",
    display: { symbol: "gwei", weiPer: 1_000_000_000n },
    unitWei: UNIT_WEI * 1n
  },
  {
    id: "CASHCAT",
    address: "0x020bfC650A365f8BB26819deAAbF3E21291018b4",
    isNative: false,
    decimals: 18,
    symbol: "CASHCAT",
    label: "CASHCAT",
    /**
     * Milli-CASHCAT, so a Quick Hit card reads as 15 rather than 0.015.
     *
     * The scale was originally 1e8, picked because "100 CASHCAT" looked like a
     * tidy card price against a billion-token supply. That was a cosmetic
     * judgement standing in for an economic one: CASHCAT trades at about
     * 0.000066 ETH, so a 100-CASHCAT card cost 0.0066 ETH against 0.000001 ETH
     * for the same card in ETH — 6,600x too expensive, and a bankroll for it
     * would have needed 3.3 ETH. 15,000 puts the card at 98.6% of the ETH
     * card's value. Scales belong at parity with the asset's price, not at
     * whatever number reads well.
     */
    display: { symbol: "mCAT", weiPer: 1_000_000_000_000_000n },
    unitWei: UNIT_WEI * 15_000n
  },
  {
    id: "SPCX",
    address: "0x4a0E65A3EcceC6dBe60AE065F2e7bb85Fae35eEa",
    isNative: false,
    decimals: 18,
    symbol: "SPCX",
    label: "SPCX",
    /* One token is one tokenized share, so a card is a small fraction of one.
       Micro-units keep it whole: a Quick Hit card is 10 µSPCX. */
    display: { symbol: "\u00b5SPCX", weiPer: 1_000_000_000_000n },
    unitWei: UNIT_WEI * 10n,
    /* Verified on-chain rather than taken from documentation: the token is a
       beacon proxy (beacon 0xe10b6f6b…1b00), so the issuer can replace its
       logic for every holder in one transaction, and the implementation
       carries pause, isBlocked and burn. A paused SPCX cannot be moved by
       anyone, this house included — proven in
       contracts/test/SwapHelperFork.t.sol. The player is told because it is a
       risk no amount of care on our side can remove. */
    warning:
      "Tokenized equity. Its issuer can pause transfers, blocklist addresses, " +
      "burn tokens and change the token's own rules at any time — any of " +
      "which would leave a balance here unpayable."
  }
];

const ACTIVE_KEY = "scratchy:currency";

function restoreActive(): SettlementAsset {
  try {
    const id = localStorage.getItem(ACTIVE_KEY);
    const found = CURRENCIES.find(c => c.id === id);
    if (found) return found;
  } catch (e) {
    /* storage blocked — fall through to the default */
  }
  return CURRENCIES[0];
}

/**
 * The asset currently in play.
 *
 * Deliberately a `let`, not a `const`: ES module bindings are live, so every
 * module that imported SETTLEMENT sees the switch without any of them needing
 * to know a switch is possible. Every helper below reads it at call time, so
 * formatting and conversion follow the active asset for free.
 */
export let SETTLEMENT: SettlementAsset = restoreActive();

/** Switch assets. Returns false if the id is not one we accept. */
export function setSettlement(id: string): boolean {
  const found = CURRENCIES.find(c => c.id === id);
  if (!found) return false;
  SETTLEMENT = found;
  try {
    localStorage.setItem(ACTIVE_KEY, id);
  } catch (e) {}
  return true;
}

/** Display-unit amount (e.g. 2 for a "$2" prize) rendered in the display denomination. */
export function formatUnits_(units: number): string {
  return formatWei(unitsToWei(units));
}

/**
 * Render wei in the display denomination, grouped and with trailing zeros
 * trimmed. Exact: the division is done on the integer digits, never in float,
 * so a value can never be shown rounded to something the chain did not charge.
 */
export function formatWei(wei: bigint): string {
  const per = SETTLEMENT.display.weiPer;
  const whole = wei / per;
  const frac = wei % per;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (frac === 0n) return grouped;
  const digits = per.toString().length - 1;
  const fracStr = frac.toString().padStart(digits, "0").replace(/0+$/, "");
  return `${grouped}.${fracStr}`;
}

/** Convert wei to display units, for balances coming back from the chain. */
export function weiToUnits(wei: bigint): number {
  return Number((wei * 1_000_000n) / SETTLEMENT.unitWei) / 1e6;
}

/**
 * Parse an amount the player typed, which is in the DISPLAY denomination
 * (gwei), into internal display units.
 *
 * These are not the same scale — one unit is 1,000 gwei — so treating typed
 * input as units silently multiplies it by 1,000. Every chip and label in the
 * UI is rendered in gwei, so the number in a text field has to mean gwei too.
 */
export function displayToUnits(displayAmount: number): number {
  return (displayAmount * Number(SETTLEMENT.display.weiPer)) / Number(SETTLEMENT.unitWei);
}

/** Inverse of displayToUnits: internal units -> the denomination shown. */
export function unitsToDisplay(units: number): number {
  return (units * Number(SETTLEMENT.unitWei)) / Number(SETTLEMENT.display.weiPer);
}

/** Convert display units to wei, for deposits and allowances. */
export function unitsToWei(units: number): bigint {
  return (BigInt(Math.round(units * 1e6)) * SETTLEMENT.unitWei) / 1_000_000n;
}

export function isOnchainConfigured(): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(CHAIN_CONFIG.gameAddress);
}
